import { useEffect, useRef } from "react";

/**
 * Poll periódico com as quatro garantias que faltavam nas telas de fila e de
 * manager.
 *
 * 1. **Não empilha.** Enquanto uma volta está no ar, a próxima não sai. Com
 *    `setInterval` puro, um servidor lento acumulava requisições até o
 *    navegador estourar o limite de conexões por origem — e o servidor lento
 *    ficava mais lento por causa disso.
 * 2. **Cancela ao sair.** O `AbortSignal` de cada volta é abortado no unmount,
 *    então trocar de rota não deixa resposta órfã chegando para uma tela que
 *    não existe mais.
 * 3. **Cala a boca em aba escondida.** `document.hidden` pausa o ciclo; ao
 *    voltar para a aba, uma volta sai na hora (o dado na tela está velho).
 * 4. **Recua quando o servidor sofre.** Se uma volta passa de `slowMs`, o
 *    intervalo dobra até `maxIntervalMs`; quando volta a responder rápido, o
 *    intervalo volta ao normal na mesma hora. Poll não pode ser o que impede o
 *    servidor de se recuperar.
 */
export function usePolling(
  tick: (signal: AbortSignal) => Promise<void>,
  options: {
    intervalMs: number;
    enabled?: boolean;
    /** Acima disso a resposta é considerada lenta e o intervalo recua. */
    slowMs?: number;
    /** Teto do recuo. Padrão: 8× o intervalo base. */
    maxIntervalMs?: number;
  },
): void {
  const { intervalMs, enabled = true, slowMs = 2_000 } = options;
  const maxIntervalMs = options.maxIntervalMs ?? intervalMs * 8;

  // `tick` costuma ser recriado a cada render; guardá-lo numa ref evita
  // reiniciar o ciclo (e perder o backoff) a cada mudança de estado da tela.
  const tickRef = useRef(tick);
  tickRef.current = tick;

  useEffect(() => {
    if (!enabled) return;

    let parado = false;
    let emVoo = false;
    let atrasoAtual = intervalMs;
    let timer: number | undefined;
    let controller: AbortController | null = null;

    const agendar = (atraso: number) => {
      if (parado) return;
      window.clearTimeout(timer);
      timer = window.setTimeout(() => void rodar(), atraso);
    };

    const rodar = async () => {
      if (parado || emVoo) return;
      if (typeof document !== "undefined" && document.hidden) {
        // Sem reagendar: `visibilitychange` retoma. Um timer girando numa aba
        // escondida é tráfego que ninguém está olhando.
        return;
      }
      emVoo = true;
      controller = new AbortController();
      const inicio = Date.now();
      try {
        await tickRef.current(controller.signal);
      } catch {
        // Erro de uma volta não derruba o ciclo; quem chamou trata o que
        // precisa mostrar na tela.
      } finally {
        emVoo = false;
        const decorrido = Date.now() - inicio;
        atrasoAtual =
          decorrido > slowMs
            ? Math.min(maxIntervalMs, Math.max(atrasoAtual * 2, intervalMs * 2))
            : intervalMs;
        agendar(atrasoAtual);
      }
    };

    const aoTrocarVisibilidade = () => {
      if (document.hidden) {
        window.clearTimeout(timer);
        return;
      }
      // Voltou para a aba: o que está na tela é velho.
      agendar(0);
    };

    document.addEventListener("visibilitychange", aoTrocarVisibilidade);
    agendar(intervalMs);

    return () => {
      parado = true;
      window.clearTimeout(timer);
      controller?.abort();
      document.removeEventListener("visibilitychange", aoTrocarVisibilidade);
    };
  }, [enabled, intervalMs, slowMs, maxIntervalMs]);
}

/**
 * Garante **no máximo uma** execução de `acao` a cada `intervalMs`, sem deixar
 * de executá-la enquanto os disparos continuam chegando.
 *
 * Existe para o WebSocket: o backend faz broadcast de todo evento de job para
 * todos os clientes, e uma fila de 200 renders terminando gerava um refresh da
 * listagem por evento, em cada aba aberta.
 *
 * Não é um debounce. Um debounce trailing puro tem um efeito perverso aqui:
 * enquanto os eventos continuarem chegando mais rápido que a janela — que é
 * exatamente o que acontece numa fila grande, o caso que este código existe
 * para tratar — o timer reinicia para sempre e a tela **nunca** atualiza.
 * Aqui o primeiro disparo passa na hora e os seguintes são espaçados: a lista
 * anda durante a fila, gastando um par de requests a cada `intervalMs`.
 */
export function useCoalescedTrigger(
  acao: () => void,
  intervalMs: number,
): () => void {
  const acaoRef = useRef(acao);
  acaoRef.current = acao;
  const intervaloRef = useRef(intervalMs);
  intervaloRef.current = intervalMs;

  const ultimoRef = useRef(0);
  const timerRef = useRef<number | undefined>(undefined);

  useEffect(() => () => window.clearTimeout(timerRef.current), []);

  return useRef(() => {
    if (timerRef.current !== undefined) return; // já há um disparo agendado
    const desdeUltimo = Date.now() - ultimoRef.current;
    const espera = Math.max(0, intervaloRef.current - desdeUltimo);
    if (espera === 0) {
      ultimoRef.current = Date.now();
      acaoRef.current();
      return;
    }
    timerRef.current = window.setTimeout(() => {
      timerRef.current = undefined;
      ultimoRef.current = Date.now();
      acaoRef.current();
    }, espera);
  }).current;
}
